import React, { useContext } from "react";
import _ from "lodash";
import Button from "Components/button/Button";
import { displayFormattedDate } from "Utils/dateFormats";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import TranslatedString from "Components/TranslatedString";
import withConfirmationModal from "Components/withConfirmationModal";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import { Icon, addImport, plus, imports as importsIcon, trash } from "Icons/index";
import { t } from "i18next";
import cx from "classnames";
import s from "./listImports.scss";
import { useApiMutation, useApiQuery } from "../../hooks";
import { useLocation, useHistory } from "react-router-dom";
import { AuthContext, MediaQueriesContext } from "../../App";
import { alertSuccess } from "Utils/alerts";

const ImportListScreen = ({ openConfirmationModal }) => {
  const location = useLocation();
  const history = useHistory();

  const {
    data: imports,
    pagination,
    isLoading: isImportsLoading,
    getData: getImportsData,
    refreshData: refreshImportsData,
    isRefreshing: isImportsRefreshing,
    updateData: updateImports,
  } = useApiQuery("getImports", {
    enablePagination: true,
    autoRefresh: {
      shouldRefresh: (data) => data.some((imp) => imp.status === "validating"),
      interval: 4000,
    },
  });

  const { mutate: deleteImport } = useApiMutation("deleteImport", {
    optimistic: (code) => updateImports((items) => items.filter((it) => it.code !== code)),
  });

  const showResources = (code) => {
    history.push({
      pathname: `/imports/${code}/resources`,
      state: { prevPagination: location.search },
    });
  };

  const addNew = () => {
    history.push({
      pathname: "/imports/add",
      state: { prevPagination: location.search },
    });
  };

  const handleDelete = async (code) => {
    await deleteImport(code);
    await refreshImportsData();
    alertSuccess(t("alert.import.deleted"));
  };

  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);

  const { isLoggedUserAdmin } = useContext(AuthContext);

  return (
    <div className={s.listImports}>
      <div className={s.headerList}>
        <div className={cx("sectionTitle", s.titleList)}>
          <TranslatedString>imports.list.header.title</TranslatedString>
        </div>

        {!isMediaMobile && (
          <div className={cx("sectionDesc", s.descList)}>
            <TranslatedString>imports.list.header.dsc</TranslatedString>
          </div>
        )}

        <div className={s.actionsList}>
          <Button docsKey="imports" color="docs" className={s.btnList} onlyIcon={isMediaMobile}>
            button.docs
          </Button>
          <Button icon={plus} onClick={addNew} onlyIcon={isMediaMobile}>
            button.newImport
          </Button>
        </div>
      </div>
      <div className={s.contentList}>
        <FullPaginationControls
          showCustomerSelection={isLoggedUserAdmin()}
          className={s.filters}
          paginationInfo={pagination}
          location={location}
          refreshList={() => getImportsData()}
          listFilters={["all", "valid", "invalid", "validating"]}
          listFilterLegends={{
            all: <Icon svg={importsIcon} width="0.75rem" height="0.75rem" />,
            valid: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
            invalid: <DomainStatusBadge status="failed" isHealthy showAsPreview />,
            validating: <DomainStatusBadge status="updating" isHealthy showAsPreview />,
          }}
          isLoading={isImportsLoading}
          showPaginationFilter={false}
        />

        <div className={s.table}>
          <ResponsiveTable
            verticalOnMobile
            history={history}
            areRowsLoading={!isImportsRefreshing && isImportsLoading}
            location={location}
            addIcon={addImport}
            addMsg={t("table.row.add.msg.import")}
            addDesc={t("table.row.add.desc.import")}
            headers={[
              {
                name: t("table.header.status"),
                size: isMobileDevice ? "2rem" : "4rem",
                center: true,
              },
              { name: t("table.header.owner"), size: "18rem" },
              { name: t("table.header.code"), size: "6rem" },
              { name: t("table.header.type"), size: "8rem" },
              { name: t("table.header.resources"), size: "4rem" },
              { name: t("table.header.errors") },
              { name: t("table.header.date"), size: "8rem" },
              { size: "3rem", center: true },
            ]}
            rows={_.map(imports, (imp) => {
              let button;
              if (imp.status !== "validating") {
                button = (
                  <ButtonIcon
                    icon={trash}
                    onClick={() =>
                      openConfirmationModal(
                        imp,
                        () => handleDelete(imp.code),
                        <TranslatedString subs={{ importCode: imp.code }}>modal.delete.import.text</TranslatedString>
                      )
                    }
                  />
                );
              }
              return (
                <tr key={imp.code}>
                  <td data-label={t("table.header.status")}>
                    <DomainStatusBadge status={imp.status} isHealthy />
                  </td>
                  <td data-label={t("table.header.owner")}>{imp.owner}</td>
                  <td data-label={t("table.header.code")}>{imp.code}</td>
                  <td data-label={t("table.header.type")}>{imp.type.split("_").join(" ")}</td>
                  <td data-label={t("table.header.resources")}>
                    {imp.resources !== 0 ? (
                      <a className="inlineLink" onClick={() => showResources(imp.code)}>
                        {imp.resources}
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td
                    data-label={t("table.header.errors")}
                    style={{
                      wordBreak: "break-word",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: _.uniq(imp.error_details).join("<br/><br/>"),
                    }}
                  ></td>
                  <td data-label={t("table.header.date")}>{displayFormattedDate(imp.created_at)}</td>
                  <td>{button}</td>
                </tr>
              );
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default withConfirmationModal(ImportListScreen, {
  title: <TranslatedString>modal.delete.import.title</TranslatedString>,
  buttonLabel: "button.delete",
});
